import type { TFunction } from 'i18next'
import type { LeadStatus } from '../services'

export type ClassifyFormFieldKey =
    | 'contact_source_name'
    | 'contact_source_email'
    | 'contact_source_phone'
    | 'contact_source_notes'
    | 'contact_source_interested_services'

export type ClassifyFormValues = Partial<
    Record<ClassifyFormFieldKey, string | string[] | null | undefined>
>

/**
 * A required rule is either:
 * - A single field key (AND — must be filled)
 * - An array of field keys (OR — at least one must be filled)
 *
 * Keep in sync with ClassifyLeadStatus in
 * datacentral-api-v4/modules/customer/customer.marketing/customer.marketing.controllers.go
 */
type RequiredRule = ClassifyFormFieldKey | ClassifyFormFieldKey[]

const CLASSIFY_REQUIRED_BY_STATUS: Record<LeadStatus, readonly RequiredRule[]> =
    {
        'warm-lead': [
            'contact_source_name',
            ['contact_source_email', 'contact_source_phone'],
            'contact_source_interested_services',
            'contact_source_notes',
        ],
        'sale-qualified-lead': [
            'contact_source_name',
            'contact_source_email',
            'contact_source_phone',
            'contact_source_interested_services',
            'contact_source_notes',
        ],
        'cool-lead': [
            'contact_source_name',
            ['contact_source_email', 'contact_source_phone'],
            'contact_source_notes',
        ],
        'un-qualified-lead': ['contact_source_name', 'contact_source_notes'],
    }

const FIELD_I18N_KEYS: Record<ClassifyFormFieldKey, string> = {
    contact_source_name: 'contact-source-name',
    contact_source_email: 'email',
    contact_source_phone: 'contact-source-phone',
    contact_source_notes: 'note',
    contact_source_interested_services: 'contact-source-interested-services',
}

function isFieldEmpty(value: string | string[] | null | undefined): boolean {
    if (value == null) return true
    if (Array.isArray(value)) return value.length === 0
    return String(value).trim() === ''
}

export function getClassifyMissingFields(
    status: LeadStatus,
    values: ClassifyFormValues,
    t: TFunction
): string[] {
    const missing: string[] = []

    for (const rule of CLASSIFY_REQUIRED_BY_STATUS[status]) {
        if (Array.isArray(rule)) {
            // OR group: at least one must be filled
            const allEmpty = rule.every((key) => isFieldEmpty(values[key]))
            if (allEmpty) {
                missing.push(
                    rule
                        .map((key) => t(FIELD_I18N_KEYS[key]))
                        .join(` ${t('or')} `)
                )
            }
        } else {
            // AND: must be filled
            if (isFieldEmpty(values[rule])) {
                missing.push(t(FIELD_I18N_KEYS[rule]))
            }
        }
    }

    return missing
}

export function getClassifyLeadStatusLabel(
    status: LeadStatus,
    t: TFunction
): string {
    switch (status) {
        case 'warm-lead':
            return t('lead_flow.warm_lead')
        case 'sale-qualified-lead':
            return t('lead_flow.sql')
        case 'cool-lead':
            return t('lead_flow.cool_lead')
        case 'un-qualified-lead':
            return t('lead_flow.unqualified')
    }
}

/**
 * Returns required-fields summary grouped by status, for rendering a legend.
 */
function ruleToLabel(rule: RequiredRule, t: TFunction): string {
    if (Array.isArray(rule)) {
        return rule.map((key) => t(FIELD_I18N_KEYS[key])).join(` ${t('or')} `)
    }
    return t(FIELD_I18N_KEYS[rule])
}

export function getClassifyRequiredFieldsSummary(t: TFunction) {
    return [
        {
            statuses: ['warm-lead'] as LeadStatus[],
            label: t('lead_flow.warm_lead'),
            fields: CLASSIFY_REQUIRED_BY_STATUS['warm-lead'].map((rule) =>
                ruleToLabel(rule, t)
            ),
        },
        {
            statuses: ['sale-qualified-lead'] as LeadStatus[],
            label: t('lead_flow.sql'),
            fields: CLASSIFY_REQUIRED_BY_STATUS['sale-qualified-lead'].map(
                (rule) => ruleToLabel(rule, t)
            ),
        },
        {
            statuses: ['cool-lead'] as LeadStatus[],
            label: t('lead_flow.cool_lead'),
            fields: CLASSIFY_REQUIRED_BY_STATUS['cool-lead'].map((rule) =>
                ruleToLabel(rule, t)
            ),
        },
        {
            statuses: ['un-qualified-lead'] as LeadStatus[],
            label: t('lead_flow.unqualified'),
            fields: CLASSIFY_REQUIRED_BY_STATUS['un-qualified-lead'].map(
                (rule) => ruleToLabel(rule, t)
            ),
        },
    ]
}
